"use client";

import React, { useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { useMediaQuery } from "react-responsive";

import styles from "./Navbar.module.scss";
import type { NavbarProps } from "./interface";

// Helpers
import classnames from "classnames";
import useOutsideClick from "@/app/[lang]/utils/useOutsideClick";

// Components
import { Flex } from "@/app/[lang]/components/ui/AntdLite";
import Logo from "../ui/Logo/Logo";
import SearchIcon from "../ui/SearchIcon";
import { NavbarMobile } from "./NavbarMobile";
import Image from "@/app/[lang]/components/shared/SmartImage";
import ArrowLeft from "../../../../../public/arrow-left.svg";

const NavbarDesktop = dynamic(() => import("./NavbarDesktop"), {
  ssr: false,
  loading: () => null,
});

// Search (antd Input/Button + search store) loads on first search-icon click
// instead of shipping in the initial bundle of every page.
const Search = dynamic(() => import("../Search"), {
  ssr: false,
  loading: () => null,
});

const SearchBackground = dynamic(
  () => import("../Search").then((m) => m.default.Background),
  { ssr: false, loading: () => null }
);

const Navbar:React.FC<NavbarProps> = (props) => {
  const { logoUrl, logoText, navigationButton = [] } = props;

  const pathname = usePathname();
  const isSearchPage = pathname.includes("cauta");

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Once opened, keep Search mounted so its close transition still runs.
  const [searchEverOpened, setSearchEverOpened] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedNavigation, setSelectedNavigation] = useState<number>();
  const [menuStack, setMenuStack] = useState<any[]>([]);
  const [isMounted, setIsMounted] = useState(false);

  const [isHidden, setIsHidden] = useState(false);
  const [isFixed, setIsFixed] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    let scrollY = window.scrollY;
    let lastScrollY = scrollY;

    const callback = () => {
      lastScrollY = scrollY;
      scrollY = window.scrollY;

      let isScrollingDown = scrollY > lastScrollY;
      let shouldHideNavbar = scrollY > 300 && isScrollingDown

      setIsHidden( shouldHideNavbar )

      // adding .is-fixed conditionally for better display when overscrolling on Mac OS
      setIsFixed( scrollY > 0 )
    };

    window.addEventListener('scroll', callback)

    return () => {
      window.removeEventListener('scroll', callback)
    }
  }, [])

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsDrawerOpen(false);
  }, [pathname]);

  const activeMenu = menuStack[menuStack.length - 1] || null;
  const isDesktop = useMediaQuery({ minWidth: 991 });
  const showDesktopNav = isMounted && isDesktop;

  const toggleSearchBar = useCallback(() => {

    if ( isSearchPage ) {
      return null;
    }

    setIsDrawerOpen(false);
    setIsMenuOpen(false);
    setSelectedNavigation(undefined);
    if (!isSearchOpen) {
      setSearchEverOpened(true);
    }
    setIsSearchOpen(!isSearchOpen);
  }, [isSearchOpen, isSearchPage])

  const containerClassnames = classnames( styles.container, {
    'is-fixed': isFixed,
    'is-hidden': isHidden && ! isSearchOpen
  } )

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  useOutsideClick(wrapperRef, () => {
    if(isMenuOpen) {
      setIsMenuOpen(false);
    }
  });

  return (
    <>
    <div className={ classnames( styles.wrapper ) }>
      <div className={ containerClassnames } ref={wrapperRef}>
        <div className={ classnames( styles.navbar, 'bg-dark-blue text-white' ) }>
          {(activeMenu && isDrawerOpen)?
              <>
                <div
                    className={styles.backNavigation}
                    onClick={() =>
                        setMenuStack((prev: any) => prev.slice(0, -1))
                    }
                >
                  <Image src={ArrowLeft} height={24} width={24} alt="arrow left" loading="lazy" decoding="async" />
                  <span className={styles.menuItem}>{activeMenu?.title}</span>
                </div>
                <NavbarMobile
                    navigationButton={navigationButton}
                    isDrawerOpen={isDrawerOpen}
                    setIsDrawerOpen={setIsDrawerOpen}
                    menuStack={menuStack}
                    setMenuStack={setMenuStack}
                    activeMenu={activeMenu}
                />
              </>
              :
              <>
                <div className={styles.logoWrapper}>
                  <Logo src={logoUrl} alt={logoText}/>
                </div>

                {showDesktopNav ? (
                        <NavbarDesktop
                            selectedNavigation={selectedNavigation}
                            setSelectedNavigation={setSelectedNavigation}
                            navigationButton={navigationButton}
                            isMenuOpen={isMenuOpen}
                            setIsMenuOpen={setIsMenuOpen}
                            setIsSearchOpen={setIsSearchOpen}
                        />
                    ) :

                    <NavbarMobile
                        navigationButton={navigationButton}
                        isDrawerOpen={isDrawerOpen}
                        setIsDrawerOpen={setIsDrawerOpen}
                        menuStack={menuStack}
                        setMenuStack={setMenuStack}
                        activeMenu={activeMenu}
                    />
                }

                <Flex className={styles.searchIconWrapper} onClick={toggleSearchBar}>
                  <SearchIcon height={32} width={32}/>
                </Flex>

                {(isSearchOpen || searchEverOpened) && <Search isVisible={ isSearchOpen } handleClose={() => setIsSearchOpen(false)} />}
              </>
          }
          </div>
      </div>
    </div>
    {(isSearchOpen || searchEverOpened) && <SearchBackground isVisible={ isSearchOpen } onClick={ () => setIsSearchOpen(false) } />}
    </>
  );
}

export default Navbar;
